import {
    Box,
    Chip,
    CircularProgress,
    ClickAwayListener,
    Collapse,
    Divider,
    IconButton,
    ListItemIcon,
    ListItemText,
    MenuItem,
    MenuList,
    Paper,
    Popper,
    Stack,
    Typography,
} from '@mui/material';
import { CircleMinus, Download, Menu, Plus, Repeat } from 'lucide-react';
import { useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useRecoilValue } from 'recoil';
import AvailableImageItem from '~/components/ImageItem/AvailableImageItem';
import ImageItem from '~/components/ImageItem/ImageItem';
import SensitiveInfoTypography from '~/components/SensitiveInfoTypography';
import usePrimaryImage from '~/hooks/image/usePrimaryImage';
import useSecondaryImage from '~/hooks/image/useSecondaryImage';
import { useSelectedRegistrationObject } from '~/hooks/registration/useRegistrationStore';
import useViewportEnvironment from '~/hooks/useViewportEnvironment';
import {
    primaryImageState,
    secondaryImageState,
} from '~/state/atoms/image/imageAtoms';
import { downloadImageToDisk } from '~/utils/io/files/downloadDicom';
import { transform3DImageData } from '~/utils/transforms/imageDataTransforms';

const modalityChipSx = {
    fontSize: 8,
    fontWeight: 400,
    lineHeight: 1,
    maxHeight: '12px',
    px: 0,
};

export default function ImageHeader() {
    const intl = useIntl();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isPrimaryExpanded, setIsPrimaryExpanded] = useState(false);
    const [isSecondaryExpanded, setIsSecondaryExpanded] = useState(false);
    const [isBlendListOpen, setIsBlendListOpen] = useState(false);
    const [downloadLoading, setDownloadLoading] = useState(false);
    const [secondaryDownloadLoading, setSecondaryDownloadLoading] =
        useState(false);
    const headerRef = useRef<HTMLDivElement>(null);

    const primaryImage = useRecoilValue(primaryImageState);
    const secondaryImage = useRecoilValue(secondaryImageState);
    const { removePrimaryImage, removeEnvironmentSecondaryImage } =
        useViewportEnvironment();
    const { availableImages, handleChangePrimaryImage } = usePrimaryImage(true);
    const { secondaryImages, handleChangeSecondaryImage } = useSecondaryImage();
    const activeRegistration = useSelectedRegistrationObject();

    if (!primaryImage) {
        return null;
    }

    const { modality, series } = transform3DImageData(primaryImage);
    const seriesDescription =
        series.description || intl.formatMessage({ id: 'unknown.series' });

    const handleToggleMenu = () => {
        setIsMenuOpen(!isMenuOpen);
    };

    const handleCloseMenu = () => {
        setIsMenuOpen(false);
        setIsPrimaryExpanded(false);
        setIsSecondaryExpanded(false);
    };

    const handleClickAway = (event: MouseEvent | TouchEvent) => {
        const target = event.target as HTMLElement;
        if (
            target === document.body ||
            target.closest('[role="presentation"]') ||
            target.closest('[role="dialog"]')
        ) {
            return;
        }
        handleCloseMenu();
    };

    const handleDownloadPrimary = async () => {
        setDownloadLoading(true);
        try {
            await downloadImageToDisk(primaryImage);
        } finally {
            setDownloadLoading(false);
        }
    };

    const handleUnloadPrimary = () => {
        removePrimaryImage();
        handleCloseMenu();
    };

    const handleDownloadSecondary = async () => {
        if (secondaryImage) {
            setSecondaryDownloadLoading(true);
            try {
                await downloadImageToDisk(secondaryImage);
            } finally {
                setSecondaryDownloadLoading(false);
            }
        }
    };

    const handleUnloadSecondary = () => {
        removeEnvironmentSecondaryImage();
    };

    const secondaryData = secondaryImage
        ? transform3DImageData(secondaryImage)
        : null;

    const sharedFor =
        secondaryImage &&
        primaryImage.frameOfReferenceUid === secondaryImage.frameOfReferenceUid;

    return (
        <>
            <Box
                ref={headerRef}
                width="100%"
                position="relative"
                overflow="hidden"
                data-testid="image-header"
            >
                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    px={1.5}
                    mr={-0.5}
                >
                    <Typography fontSize={12} color="primary" fontWeight={500}>
                        <FormattedMessage id="images" />
                    </Typography>
                    <IconButton
                        size="small"
                        onClick={handleToggleMenu}
                        selected={isMenuOpen}
                        sx={{ padding: 0.5, color: 'primary.main' }}
                        data-testid="image-menu-button"
                        aria-label={intl.formatMessage({ id: 'imageOptions' })}
                    >
                        <Menu size={14} />
                    </IconButton>
                </Box>
                <Stack px={1.5} gap={0.5}>
                    <ImageItem image={primaryImage} />
                    {secondaryImage && (
                        <ImageItem
                            image={secondaryImage}
                            isSecondary
                            hasSharedFrameOfReference={!!sharedFor}
                            registration={activeRegistration}
                            onUnlink={handleUnloadSecondary}
                        />
                    )}
                    {!secondaryImage && secondaryImages.length > 0 && (
                        <>
                            <Typography
                                fontSize={11}
                                color="primary"
                                sx={{
                                    cursor: 'pointer',
                                    '&:hover': {
                                        textDecoration: 'underline',
                                    },
                                }}
                                onClick={() =>
                                    setIsBlendListOpen(!isBlendListOpen)
                                }
                            >
                                + <FormattedMessage id="blendImages" />
                            </Typography>
                            <Collapse in={isBlendListOpen}>
                                <Box
                                    display="flex"
                                    flexDirection="column"
                                    maxHeight={300}
                                    overflow="auto"
                                >
                                    {secondaryImages.flatMap((imgReg) => {
                                        const registrations =
                                            imgReg.registrations;
                                        if (
                                            !registrations ||
                                            registrations.length === 0
                                        ) {
                                            return (
                                                <ImageItem
                                                    key={`${imgReg.image.dicomUid}-no-reg`}
                                                    image={imgReg.image}
                                                    isSelected={false}
                                                    hasSharedFrameOfReference
                                                    onSelect={() => {
                                                        handleChangeSecondaryImage(
                                                            imgReg.image,
                                                            undefined
                                                        );
                                                        setIsBlendListOpen(
                                                            false
                                                        );
                                                    }}
                                                />
                                            );
                                        }
                                        return registrations.map(
                                            (registration) => (
                                                <ImageItem
                                                    key={`${imgReg.image.dicomUid}-${registration?.sopInstanceUid ?? 'no-reg'}`}
                                                    image={imgReg.image}
                                                    isSelected={false}
                                                    registration={registration}
                                                    onSelect={() => {
                                                        handleChangeSecondaryImage(
                                                            imgReg.image,
                                                            registration
                                                        );
                                                        setIsBlendListOpen(
                                                            false
                                                        );
                                                    }}
                                                />
                                            )
                                        );
                                    })}
                                </Box>
                            </Collapse>
                        </>
                    )}
                </Stack>
            </Box>

            <Popper
                open={isMenuOpen}
                anchorEl={headerRef.current}
                placement="right-start"
                sx={{ zIndex: 1300 }}
                modifiers={[
                    {
                        name: 'offset',
                        options: { offset: [-9, 1] },
                    },
                ]}
            >
                <ClickAwayListener onClickAway={handleClickAway}>
                    <Paper
                        elevation={0}
                        sx={(theme) => ({
                            bgcolor: theme.palette.background.default,
                            border: `1px solid ${theme.palette.divider}`,
                            borderLeft: 'none',
                            borderRadius: 0,
                        })}
                    >
                        <MenuList
                            sx={{
                                '& .MuiMenuItem-root': {
                                    px: 2.5,
                                    py: 1,
                                    borderRadius: 0,
                                },
                            }}
                        >
                            <Box px={1.5} pt={1} pb={0.5}>
                                <Typography
                                    fontSize={11}
                                    color="primary"
                                    fontWeight={500}
                                >
                                    <FormattedMessage id="primaryImage" />
                                </Typography>
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    gap={0.5}
                                >
                                    <Chip
                                        label={modality}
                                        size="small"
                                        sx={modalityChipSx}
                                    />
                                    <Typography
                                        fontSize={11}
                                        color="text.secondary"
                                        noWrap
                                    >
                                        <SensitiveInfoTypography>
                                            {seriesDescription}
                                        </SensitiveInfoTypography>
                                    </Typography>
                                </Box>
                            </Box>
                            {!secondaryImage && secondaryImages.length > 0 && (
                                <>
                                    <MenuItem
                                        onClick={() => {
                                            setIsSecondaryExpanded(
                                                !isSecondaryExpanded
                                            );
                                            setIsPrimaryExpanded(false);
                                        }}
                                        selected={isSecondaryExpanded}
                                    >
                                        <ListItemIcon>
                                            <Plus size={14} />
                                        </ListItemIcon>
                                        <ListItemText>
                                            <FormattedMessage id="addSecondaryImage" />
                                        </ListItemText>
                                    </MenuItem>
                                    <Collapse in={isSecondaryExpanded}>
                                        <Box
                                            display="flex"
                                            flexDirection="column"
                                            maxHeight={300}
                                            overflow="auto"
                                        >
                                            {secondaryImages.flatMap(
                                                (imgReg) => {
                                                    const registrations =
                                                        imgReg.registrations;
                                                    if (
                                                        !registrations ||
                                                        registrations.length ===
                                                            0
                                                    ) {
                                                        return (
                                                            <ImageItem
                                                                key={`${imgReg.image.dicomUid}-no-reg`}
                                                                image={
                                                                    imgReg.image
                                                                }
                                                                isSelected={
                                                                    false
                                                                }
                                                                hasSharedFrameOfReference
                                                                onSelect={() => {
                                                                    handleChangeSecondaryImage(
                                                                        imgReg.image,
                                                                        undefined
                                                                    );
                                                                    handleCloseMenu();
                                                                }}
                                                            />
                                                        );
                                                    }
                                                    return registrations.map(
                                                        (registration) => (
                                                            <ImageItem
                                                                key={`${imgReg.image.dicomUid}-${registration?.sopInstanceUid ?? 'no-reg'}`}
                                                                image={
                                                                    imgReg.image
                                                                }
                                                                isSelected={
                                                                    false
                                                                }
                                                                registration={
                                                                    registration
                                                                }
                                                                onSelect={() => {
                                                                    handleChangeSecondaryImage(
                                                                        imgReg.image,
                                                                        registration
                                                                    );
                                                                    handleCloseMenu();
                                                                }}
                                                            />
                                                        )
                                                    );
                                                }
                                            )}
                                        </Box>
                                    </Collapse>
                                </>
                            )}
                            {availableImages.length > 1 && (
                                <>
                                    <MenuItem
                                        onClick={() => {
                                            setIsPrimaryExpanded(
                                                !isPrimaryExpanded
                                            );
                                            setIsSecondaryExpanded(false);
                                        }}
                                        selected={isPrimaryExpanded}
                                        data-testid="change-image-button"
                                    >
                                        <ListItemIcon>
                                            <Repeat size={14} />
                                        </ListItemIcon>
                                        <ListItemText>
                                            <FormattedMessage id="changeImage" />
                                        </ListItemText>
                                    </MenuItem>
                                    <Collapse in={isPrimaryExpanded}>
                                        <Box
                                            display="flex"
                                            flexDirection="column"
                                            maxHeight={300}
                                            overflow="auto"
                                        >
                                            {availableImages.map((img) => (
                                                <AvailableImageItem
                                                    key={
                                                        img.imageObject
                                                            ?.dicomUid ||
                                                        img.seriesInstanceUID
                                                    }
                                                    image={img}
                                                    isSelected={
                                                        primaryImage
                                                            .seriesMetadata
                                                            .seriesInstanceUID ===
                                                        img.seriesInstanceUID
                                                    }
                                                    onSelect={() => {
                                                        handleChangePrimaryImage(
                                                            img
                                                        );
                                                        handleCloseMenu();
                                                    }}
                                                />
                                            ))}
                                        </Box>
                                    </Collapse>
                                </>
                            )}
                            <MenuItem
                                onClick={handleDownloadPrimary}
                                disabled={downloadLoading}
                            >
                                <ListItemIcon>
                                    {downloadLoading ? (
                                        <CircularProgress
                                            size={14}
                                            color="primary"
                                        />
                                    ) : (
                                        <Download size={14} />
                                    )}
                                </ListItemIcon>
                                <ListItemText>
                                    <FormattedMessage id="download" />
                                </ListItemText>
                            </MenuItem>
                            <MenuItem
                                onClick={handleUnloadPrimary}
                                data-testid="unload-image-button"
                            >
                                <ListItemIcon>
                                    <CircleMinus size={14} />
                                </ListItemIcon>
                                <ListItemText>
                                    <FormattedMessage id="common.unload" />
                                </ListItemText>
                            </MenuItem>
                        </MenuList>

                        {secondaryImage && (
                            <>
                                <Divider />
                                <MenuList
                                    sx={{
                                        '& .MuiMenuItem-root': {
                                            px: 2.5,
                                            py: 1,
                                            borderRadius: 0,
                                        },
                                    }}
                                >
                                    <Box px={1.5} pt={1} pb={0.5}>
                                        <Typography
                                            fontSize={11}
                                            color="primary"
                                            fontWeight={500}
                                        >
                                            <FormattedMessage id="secondaryImage" />
                                        </Typography>
                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            gap={0.5}
                                        >
                                            <Chip
                                                label={secondaryData?.modality}
                                                size="small"
                                                sx={modalityChipSx}
                                            />
                                            <Typography
                                                fontSize={11}
                                                color="text.secondary"
                                                noWrap
                                            >
                                                <SensitiveInfoTypography>
                                                    {secondaryData?.series
                                                        .description ||
                                                        intl.formatMessage({
                                                            id: 'unknown.series',
                                                        })}
                                                </SensitiveInfoTypography>
                                            </Typography>
                                        </Box>
                                    </Box>
                                    {secondaryImages.length > 0 && (
                                        <>
                                            <MenuItem
                                                onClick={() => {
                                                    setIsSecondaryExpanded(
                                                        !isSecondaryExpanded
                                                    );
                                                    setIsPrimaryExpanded(false);
                                                }}
                                                selected={isSecondaryExpanded}
                                            >
                                                <ListItemIcon>
                                                    <Repeat size={14} />
                                                </ListItemIcon>
                                                <ListItemText>
                                                    <FormattedMessage id="changeSecondaryImage" />
                                                </ListItemText>
                                            </MenuItem>
                                            <Collapse in={isSecondaryExpanded}>
                                                <Box
                                                    display="flex"
                                                    flexDirection="column"
                                                    maxHeight={300}
                                                    overflow="auto"
                                                >
                                                    {secondaryImages.flatMap(
                                                        (imgReg) => {
                                                            const registrations =
                                                                imgReg.registrations;
                                                            if (
                                                                !registrations ||
                                                                registrations.length ===
                                                                    0
                                                            ) {
                                                                return (
                                                                    <ImageItem
                                                                        key={`${imgReg.image.dicomUid}-no-reg`}
                                                                        image={
                                                                            imgReg.image
                                                                        }
                                                                        isSelected={
                                                                            false
                                                                        }
                                                                        hasSharedFrameOfReference
                                                                        onSelect={() => {
                                                                            handleChangeSecondaryImage(
                                                                                imgReg.image,
                                                                                undefined
                                                                            );
                                                                            handleCloseMenu();
                                                                        }}
                                                                    />
                                                                );
                                                            }
                                                            return registrations.map(
                                                                (
                                                                    registration
                                                                ) => (
                                                                    <ImageItem
                                                                        key={`${imgReg.image.dicomUid}-${registration?.sopInstanceUid ?? 'no-reg'}`}
                                                                        image={
                                                                            imgReg.image
                                                                        }
                                                                        isSelected={
                                                                            false
                                                                        }
                                                                        registration={
                                                                            registration
                                                                        }
                                                                        onSelect={() => {
                                                                            handleChangeSecondaryImage(
                                                                                imgReg.image,
                                                                                registration
                                                                            );
                                                                            handleCloseMenu();
                                                                        }}
                                                                    />
                                                                )
                                                            );
                                                        }
                                                    )}
                                                </Box>
                                            </Collapse>
                                        </>
                                    )}
                                    <MenuItem
                                        onClick={handleDownloadSecondary}
                                        disabled={secondaryDownloadLoading}
                                    >
                                        <ListItemIcon>
                                            {secondaryDownloadLoading ? (
                                                <CircularProgress
                                                    size={14}
                                                    color="primary"
                                                />
                                            ) : (
                                                <Download size={14} />
                                            )}
                                        </ListItemIcon>
                                        <ListItemText>
                                            <FormattedMessage id="download" />
                                        </ListItemText>
                                    </MenuItem>
                                    <MenuItem
                                        onClick={() => {
                                            handleUnloadSecondary();
                                            handleCloseMenu();
                                        }}
                                    >
                                        <ListItemIcon>
                                            <CircleMinus size={14} />
                                        </ListItemIcon>
                                        <ListItemText>
                                            <FormattedMessage id="common.unload" />
                                        </ListItemText>
                                    </MenuItem>
                                </MenuList>
                            </>
                        )}
                    </Paper>
                </ClickAwayListener>
            </Popper>
        </>
    );
}
